import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import * as S from './SalesChannels.styled';

const defaultColSpan = {
	md: 6
};

const fullColSpan = {
	sm: 12
};

const SalesChannelForm = NiceModal.create(({ id, modalName, mode }) => {
	const modal = useModal();
	const darkMode = useSelector((state) => state.layout.darkMode);
	const defaultImage = darkMode
		? 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptySalesChannel-darkMode.png'
		: 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptySalesChannel.png';

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				name: yup.string().nullable().required(),
				integrationName: yup.string().nullable().required(),
				groupName: yup.string().nullable(),
				description: yup.string().nullable(),
				showOnOto: yup.boolean().nullable(),
				formName: yup.string().nullable(),
				formType: yup.string(),
				v2FormName: yup.string().nullable(),
				icon: yup.string().nullable(),
				featureJSON: yup.string().nullable().json(),
				statusMappingUpdateTypes: yup.string().nullable(),
				statusMappings: yup.array().of(
					yup.object().shape({
						updateType: yup.string().nullable().required(),
						otoStatus: yup.string().nullable().required(),
						ecomStatus: yup.string().nullable(),
						ecomComment: yup.string().nullable()
					})
				),
				syncOrders: yup.boolean().nullable(),
				syncProducts: yup.boolean().nullable(),
				storeLocation: yup.boolean().nullable(),
				statusMapping: yup.boolean().nullable(),
				testable: yup.boolean().nullable(),
				syncBranches: yup.boolean().nullable(),
				slug: yup.string().nullable(),
				authorize: yup.boolean().nullable(),
				images: yup.array().of(yup.string().nullable()).nullable()
			}),
		[]
	);

	const fillForm = async () => {
		if (!id) {
			return;
		}
		const defaultValues = {
			name: null,
			integrationName: null,
			groupName: null,
			description: null,
			showOnOto: false,
			formName: null,
			v2FormName: null,
			icon: null,
			featureJSON: null,
			statusMappingUpdateTypes: null,
			statusMappings: null,
			syncOrders: false,
			syncProducts: false,
			storeLocation: false,
			statusMapping: false,
			testable: false,
			syncBranches: false,
			slug: null,
			authorize: false,
			images: null
		};

		try {
			const { data } = await axiosPrivate.post(ADMIN.SALES_CHANNELS.GET_DETAILS, { id });
			return {
				...defaultValues,
				...data
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		control,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = (data) => {
		const apiPath = mode === 'edit' ? ADMIN.SALES_CHANNELS.UPDATE_CHANNEL : ADMIN.SALES_CHANNELS.CREATE_CHANNEL;
		axiosPrivate
			.post(apiPath, data)
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.ADMIN_SALES_CHANNELS));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'create':
				return 'createSalesChannel';
			case 'edit':
				return 'editSalesChannel';
			default:
				return 'salesChannelDetails';
		}
	}, [mode]);

	const { fields, append, remove } = useFieldArray({ control, name: 'statusMappings' });
	const {
		fields: imagesFields,
		append: appendImages,
		remove: removeImages
	} = useFieldArray({ control, name: 'images' });

	const statusMappingColumns = useMemo(
		() => [
			{
				id: 'updateType',
				header: () => <StatusMappingHeader title="updateType" description="updateType.description" />,
				accessorKey: 'updateType',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						required
						placeholder="selectOne"
						name={`statusMappings[${index}].updateType`}
						value={watch(`statusMappings[${index}].updateType`)}
						enumName={ENUMS.MS_UPDATE_TYPE}
						setValue={(value) => {
							setValue(`statusMappings[${index}].updateType`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						viewMode={isViewMode}
					/>
				)
			},
			{
				id: 'otoStatus',
				header: () => <StatusMappingHeader title="otoStatus" description="otoStatus.description" />,
				accessor: 'otoStatus',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						required
						placeholder="selectOne"
						name={`statusMappings[${index}].otoStatus`}
						value={watch(`statusMappings[${index}].otoStatus`)}
						enumName={ENUMS.MAGENTO_SETTINGS_OTO_STATUS}
						setValue={(value) => {
							setValue(`statusMappings[${index}].otoStatus`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						viewMode={isViewMode}
					/>
				)
			},
			{
				id: 'ecomStatus',
				header: () => <StatusMappingHeader title="ecomStatus" description="ecomStatus.description" />,
				accessor: 'ecomStatus',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<StringInput name={`statusMappings[${index}].ecomStatus`} viewMode={isViewMode} />
				)
			},
			{
				id: 'ecomComment',
				accessorKey: 'ecomComment',
				header: () => <StatusMappingHeader title="ecomComment" description="ecomComment.description" />,
				size: 100,
				renderCell: ({ row: { index } }) => (
					<StringInput name={`statusMappings[${index}].ecomComment`} viewMode={isViewMode} />
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[]
	);
	const imagesColumns = useMemo(
		() => [
			{
				id: 'imageUrl',
				header: () => <Typography>Image URL</Typography>,
				accessorKey: 'imageUrl',
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`images[${index}]`} viewMode={isViewMode} />
			},
			{
				id: 'image',
				header: () => <Typography>Image</Typography>,
				accessorKey: 'image',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<Image url={watch(`images[${index}]`)} width="8rem" height="8rem" defaultUrl={defaultImage} />
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[]
	);

	const formatFeatureJSON = () => {
		try {
			if (!watch('featureJSON')) {
				return '-';
			}
			return JSON.stringify(JSON.parse(watch('featureJSON')), null, 4);
		} catch {
			return watch('featureJSON');
		}
	};

	return (
		<Modal fullwidth className="sales-channel-modal" name={modalName} maxWidth="medium">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<S.FormContainer>
					<Form formMethods={formMethods} borderless>
						<S.FormSubHeader>
							<Typography fontSize="1.8rem" fontWeight="600">
								salesChannel
							</Typography>
						</S.FormSubHeader>
						<FormItem colSpan={defaultColSpan} label="name" isLoading={isLoading} required>
							<StringInput name="name" placeholder="name" viewMode={isViewMode} />
						</FormItem>
						<FormItem colSpan={defaultColSpan} label="integrationName" isLoading={isLoading} required>
							<StringInput name="integrationName" placeholder="integrationName" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="slug" isLoading={isLoading}>
							<StringInput name="slug" placeholder="slug" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="groupName" isLoading={isLoading}>
							<StringInput name="groupName" placeholder="groupName" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="description" isLoading={isLoading}>
							<StringInput name="description" placeholder="description" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="formName" isLoading={isLoading}>
							<StringInput name="formName" placeholder="formName" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="v2FormName" isLoading={isLoading}>
							<StringInput name="v2FormName" placeholder="v2FormName" viewMode={isViewMode} />
						</FormItem>
						<FormItem colSpan={fullColSpan} label="formType" isLoading={isLoading}>
							<EnumSelect
								placeholder="selectOne"
								name="formType"
								value={watch('formType')}
								enumName={ENUMS.SALES_CHANNELS_FORMS}
								setValue={(value) => {
									setValue('formType', value, { shouldValidate: true, shouldDirty: true });
								}}
								viewMode={isViewMode}
							/>
						</FormItem>
						<FormItem colSpan={defaultColSpan} label="showOnOto" isLoading={isLoading} required>
							<SwitchInput name="showOnOto" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="syncOrders" isLoading={isLoading}>
							<SwitchInput name="syncOrders" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="syncProducts" isLoading={isLoading}>
							<SwitchInput name="syncProducts" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="storeLocation" isLoading={isLoading}>
							<SwitchInput name="storeLocation" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="statusMapping" isLoading={isLoading}>
							<SwitchInput name="statusMapping" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="authorize" isLoading={isLoading}>
							<SwitchInput name="authorize" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={defaultColSpan} label="testable" isLoading={isLoading}>
							<SwitchInput name="testable" viewMode={isViewMode} />
						</FormItem>
						<FormItem colSpan={defaultColSpan} label="syncBranches" isLoading={isLoading}>
							<SwitchInput name="syncBranches" viewMode={isViewMode} />
						</FormItem>

						<FormItem colSpan={fullColSpan} label="images" isLoading={isLoading}>
							<Table
								loading={isLoading}
								data={imagesFields}
								columns={imagesColumns}
								onRowDelete={(row) => removeImages(row.index)}
								onRowAdd={() => appendImages('')}
								showAddNewRowButton={!isViewMode}
								initEditMode={(row) => row.id === null}
								noRecordsFound={
									<S.NoRecordsFound>
										<Typography color="var(--content-3)">noRecordsFoundTitle</Typography>
									</S.NoRecordsFound>
								}
								isRowDeletable={() => !isViewMode}
							/>
						</FormItem>
						<FormItem
							colSpan={fullColSpan}
							label="icon"
							isLoading={isLoading}
							className={isViewMode ? 'icon-container col' : 'icon-container row'}>
							<StringInput name="icon" placeholder="icon" viewMode={isViewMode} />
							<div>
								<Image url={watch('icon')} width="8rem" height="8rem" defaultUrl={defaultImage} />
							</div>
						</FormItem>

						<FormItem colSpan={fullColSpan} label="statusMappingUpdateTypes" isLoading={isLoading}>
							<StringInput
								name="statusMappingUpdateTypes"
								placeholder="statusMappingUpdateTypes"
								viewMode={isViewMode}
							/>
						</FormItem>

						<FormItem colSpan={fullColSpan} label="featureJSON" isLoading={isLoading}>
							{!isViewMode ? (
								<TextArea
									name="featureJSON"
									placeholder="{ }"
									rows="8"
									className="json-text-area"
									maxlength={4096}
								/>
							) : (
								<Typography>
									<pre className="pre-text">{formatFeatureJSON()}</pre>
								</Typography>
							)}
						</FormItem>

						<S.FormSubHeader>
							<Typography fontSize="1.8rem" fontWeight="600">
								statusMapping
							</Typography>
						</S.FormSubHeader>

						<Table
							loading={isLoading}
							data={fields}
							columns={statusMappingColumns}
							onRowDelete={(row) => {
								remove(row.index);
							}}
							onRowAdd={() => {
								append({});
							}}
							showAddNewRowButton={!isViewMode}
							initEditMode={(row) => row.id === null}
							noRecordsFound={
								<S.NoRecordsFound>
									<Typography color="var(--content-3)">noRecordsFoundTitle</Typography>
								</S.NoRecordsFound>
							}
							isRowDeletable={() => !isViewMode}
						/>
					</Form>
				</S.FormContainer>
			</ModalContent>

			<ModalActions>
				<Button label={isViewMode ? 'close' : 'cancel'} variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

const StatusMappingHeader = ({ title, description }) => (
	<S.StatusMappingContainer>
		<Typography color="var(--black)" fontWeight="500">
			{title}
		</Typography>

		{description && (
			<Tooltip title={description}>
				<Icon icon={faCircleInfo} color="var(--content-4)" size="small" />
			</Tooltip>
		)}
	</S.StatusMappingContainer>
);

export default SalesChannelForm;
